import { IconButton } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import IcBaselineClose from '~icons/ic/baseline-close'
import LogosApple from '~icons/logos/apple'
import LogosGooglePlayIcon from '~icons/logos/google-play-icon'
import MaterialSymbolsApkDocumentRounded from '~icons/material-symbols/apk-document-rounded'
import appIconUrl from 'src/assets/app-icon-small.jpg'
import apkDownloadQRUrl from 'src/assets/app-qrcode/apk-download.jpg'
import iosDownloadQRUrl from 'src/assets/app-qrcode/ios-download.jpg'
import playStoreDownloadQRUrl from 'src/assets/app-qrcode/playstore-download.jpg'
import apkBadgeDarkUrl from 'src/assets/icon/apk-badge-dark.svg'
import appStoreBadgeDarkUrl from 'src/assets/icon/app-store-badge-dark.svg'
import playStoreBadgeDarkUrl from 'src/assets/icon/google-play-badge-dark.svg'
import { dialog } from 'src/components/Modal/DynamicModal'
import { config } from 'src/utils/config'
import { cx } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { track } from 'src/utils/tracker'

type DownloadAppModalProps = {
  hideIos?: boolean
  hideAndroid?: boolean
  hideAndroidFilterFree?: boolean
  trackingArgs?: Record<string, any>
  /** Reward amount shown in the subtitle (e.g. quest reward CTA). */
  rewardCount?: number
}

function detectUserPlatform(): 'ios' | 'android' | null {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return null
}

const cardBase =
  'group relative flex flex-col items-center justify-center rounded-xl border bg-white dark:bg-neutral-900 border-zinc-200 dark:border-zinc-700 transition-all duration-150 hover:shadow-lg hover:border-zinc-300 dark:hover:border-zinc-600 active:scale-[0.97] hover:no-underline'

const primaryRing =
  'ring-2 ring-theme-primary/70 shadow-md shadow-theme-primary/10'

export function openDownloadAppModal(props?: DownloadAppModalProps) {
  track('download_app_modal_view', { ...props?.trackingArgs })

  return new Promise<boolean | null>(resolve => {
    const instanceRef = dialog({
      children: (
        <DownloadAppModalContent
          onClose={() => instanceRef.current?.close()}
          {...props}
        />
      ),
      onClose: () => resolve,
      fullWidth: false,
      responsive: false,
      maxWidth: 'lg',
      dialogProps: {
        maxWidth: 'md',
      },
    })
  })
}

function DownloadAppModalContent(
  props: {
    onClose: () => void
  } & DownloadAppModalProps,
) {
  const { onClose, rewardCount } = props
  const { t } = useTranslation(['common', 'user'])

  const isMobile = useIsMobile()
  const userPlatform = detectUserPlatform()

  const hasReward = typeof rewardCount === 'number' && rewardCount > 0
  const subtitle = hasReward
    ? t('user:quest.download_app.subtitle', {
        count: rewardCount,
        formattedCount: rewardCount.toLocaleString(),
        defaultValue: `Get ${rewardCount.toLocaleString()} credits!`,
      })
    : null
  const instruction = hasReward
    ? t('user:quest.download_app.claim_instruction', {
        defaultValue:
          'After installing, open the app → Rewards → claim your reward',
      })
    : null

  const header = (
    <div className="relative w-full flex flex-col items-start gap-2 pb-2">
      <IconButton
        aria-label={t('common:close')}
        className="!absolute right-2 top-2 text-gray-500"
        onClick={onClose}
      >
        <IcBaselineClose />
      </IconButton>

      <div className="flex items-center gap-3 pr-12">
        <img
          src={appIconUrl}
          alt="PixAI App"
          className="size-12 flex-none rounded-xl shadow-sm"
        />
        <h1 className="text-2xl font-bold leading-tight">
          {t('common:app-download-dialog.title')}
        </h1>
      </div>

      {subtitle && (
        <h2 className="flex items-center gap-2 text-lg font-semibold text-theme-primary">
          <GameIconsCrystalBars className="size-5 flex-none" />
          <span>{subtitle}</span>
        </h2>
      )}
    </div>
  )

  const instructionBlock = instruction && (
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-neutral-900/40 px-4 py-3">
      <span className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
        {instruction}
      </span>
    </div>
  )

  return isMobile ? (
    <div
      className="w-full fixed bottom-0 left-0 right-0 rounded-t-3xl bg-background px-6 pt-6"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}
    >
      <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-zinc-300 dark:bg-zinc-600" />
      {header}
      <DownloadAppButtons {...props} isMobile primaryPlatform={userPlatform} />
      {instructionBlock && <div className="mt-4">{instructionBlock}</div>}
    </div>
  ) : (
    <div className="w-full h-full p-6 flex flex-col gap-4">
      {header}
      <DownloadAppButtons {...props} primaryPlatform={userPlatform} />
      {instructionBlock}
    </div>
  )
}

function DownloadAppButtons(
  props: StyleProps &
    Pick<
      DownloadAppModalProps,
      'hideAndroid' | 'hideAndroidFilterFree' | 'hideIos'
    > & {
      isMobile?: boolean
      trackingProps?: Record<string, any>
      primaryPlatform?: 'ios' | 'android' | null
    },
) {
  const {
    hideAndroid,
    hideAndroidFilterFree = true,
    hideIos,
    isMobile,
    trackingProps,
    primaryPlatform,
  } = props
  const { t } = useTranslation(['common'])

  const recommendedBadge = (
    <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-theme-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
      {t('common:app-download-dialog.recommended')}
    </span>
  )

  return isMobile ? (
    <div className={cx('flex flex-col gap-4 mt-4', props.className)}>
      {!hideAndroid && (
        <section className="relative">
          {primaryPlatform === 'android' && recommendedBadge}
          <h2 className="font-semibold text-sm text-zinc-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">
            Android
          </h2>
          <div className="flex flex-row flex-wrap items-center gap-3 w-full">
            <a
              className={cx(
                'h-[60px] w-[180px] overflow-hidden rounded-lg transition-transform active:scale-[0.97] hover:no-underline',
                primaryPlatform === 'android' && primaryRing,
              )}
              href={config.playStoreLink}
              download
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'android',
                  ...trackingProps,
                })
              }}
            >
              <img
                src={playStoreBadgeDarkUrl}
                className="h-full w-full object-contain"
                alt="Get it on Google Play"
              />
            </a>
            {!hideAndroidFilterFree && (
              <a
                className="h-[60px] w-[180px] overflow-hidden rounded-lg transition-transform active:scale-[0.97] hover:no-underline flex items-center justify-center gap-2 bg-black text-white"
                href={config.apkReleaseLink}
                download
                rel="noopener noreferrer"
                target="_blank"
                onClick={() => {
                  track('app_download_click', {
                    value: 'android-filter-free',
                    ...trackingProps,
                  })
                }}
              >
                <MaterialSymbolsApkDocumentRounded className="size-6" />
                <img src={apkBadgeDarkUrl} className="h-6" alt="APK" />
              </a>
            )}
          </div>
        </section>
      )}
      {!hideIos && (
        <section className="relative">
          {primaryPlatform === 'ios' && recommendedBadge}
          <h2 className="font-semibold text-sm text-zinc-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">
            iOS
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <a
              className={cx(
                'h-[60px] w-[180px] overflow-hidden rounded-lg transition-transform active:scale-[0.97] hover:no-underline',
                primaryPlatform === 'ios' && primaryRing,
              )}
              href={config.iosAppStoreLink}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'ios',
                  ...trackingProps,
                })
              }}
            >
              <img
                src={appStoreBadgeDarkUrl}
                className="h-full w-full object-contain"
                alt="Download on the App Store"
              />
            </a>
          </div>
        </section>
      )}
    </div>
  ) : (
    <div className={cx('flex flex-row gap-6 flex-wrap', props.className)}>
      {!hideAndroid && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-base text-primary">Android</h2>
            {primaryPlatform === 'android' && (
              <span className="rounded-full bg-theme-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-theme-primary">
                {t('common:app-download-dialog.recommended')}
              </span>
            )}
          </div>
          <div className="flex flex-row items-start gap-3">
            <PlatformCard
              className={cx(
                cardBase,
                primaryPlatform === 'android' && primaryRing,
              )}
              href={config.playStoreLink}
              download
              onClick={() => {
                track('app_download_click', {
                  value: 'android',
                  ...trackingProps,
                })
              }}
              icon={<LogosGooglePlayIcon className="size-10" />}
              storeName="Google Play"
              qrUrl={playStoreDownloadQRUrl}
            />
            {!hideAndroidFilterFree && (
              <PlatformCard
                className={cardBase}
                href={config.apkReleaseLink}
                download
                onClick={() => {
                  track('app_download_click', {
                    value: 'android-filter-free',
                    ...trackingProps,
                  })
                }}
                icon={<MaterialSymbolsApkDocumentRounded className="size-10" />}
                storeName="APK File"
                qrUrl={apkDownloadQRUrl}
              />
            )}
          </div>
        </section>
      )}
      {!hideIos && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-base text-primary">iOS</h2>
            {primaryPlatform === 'ios' && (
              <span className="rounded-full bg-theme-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-theme-primary">
                {t('common:app-download-dialog.recommended')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <PlatformCard
              className={cx(cardBase, primaryPlatform === 'ios' && primaryRing)}
              href={config.iosAppStoreLink}
              onClick={() => {
                track('app_download_click', {
                  value: 'ios',
                  ...trackingProps,
                })
              }}
              icon={<LogosApple className="size-10 dark:[&>path]:fill-white" />}
              storeName="App Store"
              qrUrl={iosDownloadQRUrl}
            />
          </div>
        </section>
      )}
    </div>
  )
}

function PlatformCard(props: {
  className: string
  href: string
  download?: boolean
  onClick: () => void
  icon: React.ReactNode
  storeName: string
  qrUrl: string
}) {
  const { className, href, download, onClick, icon, storeName, qrUrl } = props
  const [loaded, setLoaded] = useState(false)
  return (
    <a
      className={cx('w-[200px] p-3 gap-3', className)}
      href={href}
      {...(download ? { download: true } : {})}
      rel="noopener noreferrer"
      target="_blank"
      onClick={onClick}
    >
      <div className="relative size-[160px] overflow-hidden rounded-lg bg-white">
        {!loaded && (
          <div className="absolute inset-0 animate-pulse bg-zinc-200 dark:bg-zinc-700" />
        )}
        <img
          src={qrUrl}
          alt={`${storeName} QR code`}
          loading="eager"
          decoding="async"
          className={cx(
            'size-full object-contain transition-opacity duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={() => setLoaded(true)}
        />
      </div>
      <div className="flex items-center justify-center gap-2 w-full">
        <span className="flex-none [&>svg]:size-5">{icon}</span>
        <p className="font-semibold text-sm leading-5">{storeName}</p>
      </div>
    </a>
  )
}
